// Static import — #content/manifest is already statically bundled by @nuxt/content
// internals, so a dynamic import() here just creates a redundant split point and
// triggers Rollup's "dynamically imported by X but also statically imported by Y" warning.
import { checksums as rawChecksums } from "#content/manifest";

// Warms the @nuxt/content client-side SQLite dump so the FIRST in-app navigation
// (which executes a `queryCollection(...)` on the client) doesn't pay the
// dump-fetch + decompression latency. Without this hint the first nav stalls
// ~200–400 ms on the dump fetch alone.
//
// Why deferred and scoped rather than eager + broad:
//   - `rel=prefetch` resources still appear in Lighthouse's total network
//     payload audit. The previous eager-prefetch shape pulled `sections`
//     (1.2 MiB) on every cold load, even when the visitor never navigated.
//   - The locale-specific site collection (peppol, gebruik, etc.) is small
//     and high-hit, so it is still prefetched — but only after the page
//     reports idle, so it never competes with LCP/TBT-affecting resources.
//   - `sections` is intentionally NOT prefetched: it is large and only
//     needed once the user invokes search or navigates deep. The first
//     deep nav pays the ~200 ms cost; subsequent navs hit localStorage.
//
// Tunables:
//   - `IDLE_TIMEOUT_MS` caps how long we wait for `requestIdleCallback`
//     before scheduling anyway. Keeps the warm-up bounded on busy pages.
const IDLE_TIMEOUT_MS = 4000;

function schedule(fn: () => void) {
  type IdleCallback = (cb: () => void, opts?: { timeout: number }) => void;
  const ric = (window as unknown as { requestIdleCallback?: IdleCallback }).requestIdleCallback;
  if (typeof ric === "function") {
    ric(fn, { timeout: IDLE_TIMEOUT_MS });
  } else {
    // Safari < 17 has no requestIdleCallback. setTimeout(0) fires after the
    // current macrotask, so this still pushes the prefetch off the critical
    // path even on the fallback.
    setTimeout(fn, 0);
  }
}

function appendPrefetch(href: string) {
  const link = document.createElement("link");
  link.rel = "prefetch";
  link.as = "fetch";
  link.crossOrigin = "anonymous";
  link.href = href;
  document.head.appendChild(link);
}

export default defineNuxtPlugin(() => {
  const cs = rawChecksums as Record<string, string>;

  // Locale-specific site collection from /{locale}/{site}/...
  const parts = window.location.pathname.split("/").filter(Boolean);
  const site = parts.length >= 2 ? parts[1] : undefined;

  const work = () => {
    // Tiny shared collections that virtually every navigation touches.
    // `siteBlocks` is ~1 KiB and `home` is ~55 KiB — cheap to warm.
    for (const collection of ["siteBlocks", "home"]) {
      const v = cs[collection];
      if (!v) continue;
      appendPrefetch(`/__nuxt_content/${collection}/sql_dump.txt?v=${v}`);
    }
    if (site) {
      const v = cs[site];
      if (v) appendPrefetch(`/__nuxt_content/${site}/sql_dump.txt?v=${v}`);
    }
  };

  // Wait for `load` before scheduling — `requestIdleCallback` during initial
  // parse can still fire before LCP if the main thread is briefly idle
  // between resource arrivals. Waiting for `load` keeps the prefetch
  // strictly post-paint.
  if (document.readyState === "complete") {
    schedule(work);
  } else {
    window.addEventListener("load", () => schedule(work), { once: true });
  }
});
